/**
 * Canonical per-row parse-result contract — the synchronous response shape the
 * Phase 2 integrator (`schedule-change-integration`) returns from `POST /parse`,
 * the Phase 4 kanban-api bridge forwards verbatim, and the web upload screen
 * renders.
 *
 * Lives in `packages/domain` so the integrator's response typing, the bridge,
 * and the web client share ONE definition instead of copy-pasting the 5-variant
 * outcome union (RESEARCH Open Question 1).
 *
 * This is a cross-service wire contract, NOT a persisted Mongo document, so it
 * carries no `_schemaVersion`.
 */
import { z } from 'zod';

/**
 * Per-row enrichment outcome (discriminated on `outcome`). Mirrors the
 * integrator's `enrichPnr` returns EXACTLY (`api/parse.ts`):
 *   - matched           — a traveler flying the changed flight; the only variant that
 *                         publishes. A group PNR yields one per traveler.
 *   - traveler-unbound   — voucher matched but no bound traveler (ROUTE-04, fail-closed)
 *   - voucher-not-found  — no active voucher of the localizador flies the changed flight
 *   - ambiguous-locator  — the vouchers flying it belong to different clients
 *                          (`companyId`): nothing is published, the operator decides
 *   - parse-error        — row failed to parse (carries rowIndex, NO localizador)
 *   - enrichment-error   — by-locator lookup/publish threw
 */
export const RowOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('matched'),
    localizador: z.string(),
    userId: z.string(),
    companyId: z.string().nullable(),
  }),
  z.object({ outcome: z.literal('traveler-unbound'), localizador: z.string() }),
  z.object({ outcome: z.literal('voucher-not-found'), localizador: z.string() }),
  z.object({ outcome: z.literal('ambiguous-locator'), localizador: z.string() }),
  z.object({
    outcome: z.literal('parse-error'),
    rowIndex: z.number().int().nonnegative(),
    error: z.string(),
  }),
  z.object({ outcome: z.literal('enrichment-error'), localizador: z.string(), error: z.string() }),
]);
export type RowOutcome = z.infer<typeof RowOutcomeSchema>;

export const ScheduleChangeResultSchema = z.object({
  processorType: z.string(),
  totalRows: z.number().int().nonnegative(),
  rows: z.array(RowOutcomeSchema),
});
export type ScheduleChangeResult = z.infer<typeof ScheduleChangeResultSchema>;
